import { useMemo } from "react";
import { sections } from "@/react/contexts/ChecklistConstants";
import {
  createStep,
  getInitialExpandedSections,
} from "@/react/contexts/ChecklistHelpers";
import { useChecklistApi } from "@/react/hooks/useChecklistApi";
import { useChecklistStore } from "@/react/stores/useChecklistStore";
import { useChecklistActions } from "./useChecklistActions";
import { useChecklistCustomContent } from "./useChecklistCustomContent";

export const useChecklistData = () => {
  const { checklist, isChecklistLoading, errorFetchingChecklist } =
    useChecklistApi();
  const { stepActions } = useChecklistActions();
  const { getStepCustomContent } = useChecklistCustomContent();
  const { expandedSections, focusedStepKey } = useChecklistStore();

  const {
    total_steps: totalSteps,
    completed_steps: completedSteps,
    progress,
    initial_focused_step_key: initialFocusedStepKey,
    complete: isComplete,
  } = checklist ?? {};

  const sectionsWithFocus = useMemo(() => {
    if (!checklist) return [];

    const currentFocusedStepKey = focusedStepKey || initialFocusedStepKey;

    return sections.map(section => {
      const sectionSteps = section.steps.map(stepKey => {
        const step = createStep(
          checklist,
          stepKey,
          section.key,
          stepActions[stepKey],
          getStepCustomContent(stepKey),
        );

        return {
          ...step,
          focus: currentFocusedStepKey === step.key,
        };
      });

      const isExpanded = !!expandedSections[section.title];
      const isComplete = sectionSteps.every(step => step.complete);

      return {
        title: section.title,
        steps: sectionSteps,
        expanded: isExpanded,
        shouldStrikeTitle: isComplete && !isExpanded,
      };
    });
  }, [
    checklist,
    initialFocusedStepKey,
    focusedStepKey,
    expandedSections,
    stepActions,
    getStepCustomContent,
  ]);

  const initialExpandedSections = useMemo(
    () => getInitialExpandedSections(checklist, initialFocusedStepKey),
    [checklist, initialFocusedStepKey],
  );

  return {
    checklist,
    isChecklistLoading,
    errorFetchingChecklist,
    totalSteps,
    completedSteps,
    progress,
    sectionsWithFocus,
    initialFocusedStepKey,
    initialExpandedSections,
    isComplete,
  };
};
